<template>
  <section>
    <PagesBlogMainArtilesMainPreview
      v-if="!mainPostError && mainPostData?.length"
      :article="mainPost?.data?.getBlogPosts?.data?.[0] as BlogPost"
      class="mb32"
    />

    <div>
      <PagesBlogMainArtilesTopFilters
        v-if="store.categories?.length"
        class="mb20"
        @on-search="onSearch"
      />

      <PagesBlogCardsGrid v-if="posts?.length">
        <PagesBlogArticleCard
          v-for="article in posts"
          :key="article.link"
          :article="article"
        />
      </PagesBlogCardsGrid>

      <LazyUiNoResult
        v-else
        class="mxa"
      />
    </div>

    <div class="f mt20">
      <span class="f14">
        {{
          $t('show_items_of_total', {
            items: posts.length >= limit ? limit : posts.length,
            total: posts.length < data?.data?.getBlogPosts?.max_pages * limit ? posts.length : data?.data?.getBlogPosts?.max_pages * limit,
          })
        }}
      </span>

      <PaginateV2
        v-model="page"
        :pages-length="+(data?.data?.getBlogPosts?.max_pages || 0)"
        class="pt0"
        @on-page-change="fetchPosts"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import type { BlogPost } from '~/graphql/generated/schema-types';
import { EventBus } from '~/composables/bus';
import { useBlogStore } from '~/store/blog';
import { languagesEnum } from '~/types/LocalesToCockies';
import { getBlogPosts } from '~/utils/api';

const emit = defineEmits(['onEmpty', 'onPostsLoaded']);

const page = ref(1);
const limit = ref(9);

const route = useRoute();
const category = computed(() => route.params.category);
const store = useBlogStore();
const config = useRuntimeConfig();
const siteHost = config.public.siteHost;
const { locale } = useI18n();
const searchTerm = ref('');

const {
  data,
  error,
} = await useAsyncData(`blogMainArticles${category.value || 'main'}`, () => getBlogPosts({
  hidden: false,
  category: category.value as string || null,
  page: page.value,
  limit: limit.value,
  is_main: false,
  lang: languagesEnum[locale.value.toUpperCase() as keyof typeof languagesEnum],
}));

const {
  data: mainPost,
  error: mainPostError,
} = await useAsyncData(`blogMainPost`, () => getBlogPosts({
  hidden: false,
  page: 1,
  limit: 1,
  is_main: true,
  lang: languagesEnum[locale.value.toUpperCase() as keyof typeof languagesEnum],
}));

if (data.value?.errors?.length || mainPost.value?.errors?.length || error.value || mainPostError.value) {
  throw createError({
    status: 404,
    statusText: `Blog posts not found: ${data.value?.errors?.[0]?.message || mainPost.value?.errors?.[0]?.message || error.value?.message || mainPostError.value?.message}`,
  });
}
const posts = computed(() => data.value?.data?.getBlogPosts?.data || []);
const mainPostData = computed(() => mainPost.value?.data?.getBlogPosts?.data || []);

async function fetchPosts() {
  try {
    const { errors, data: fetchData } = await getBlogPosts({
      hidden: false,
      category: category.value as string || null,
      page: page.value,
      limit: limit.value,
      is_main: false,
      lang: languagesEnum[locale.value.toUpperCase() as keyof typeof languagesEnum],
      searchText: searchTerm.value,
    });

    if (errors?.length) {
      throw new Error(errors[0].message);
    }

    data.value = { data: fetchData, errors };
  } catch (e: any) {
    console.log(e.message);
    EventBus.$emit('notify', {
      success: false,
      msg: `Something went wrong: ${e.message}`,
    });
  }
}

watch([data, mainPost], () => {
  if (posts.value?.length && !mainPostData.value?.length) {
    emit('onEmpty');
  }
}, {
  immediate: true,
});

function getSchemasFromPosts(posts: BlogPost[]) {
  return posts.filter(art => art.lang === languagesEnum[locale.value.toUpperCase() as keyof typeof languagesEnum]).map(post => ({
    '@type': 'NewsArticle',
    'headline': post?.title,
    'url': `${siteHost}/blog/${post?.category?.link}/${post?.link}`,
    'image': post?.img,
    'datePublished': post?.published_at,
    'dateModified': post?.updated_at,
    'author': {
      '@type': 'Person',
      'name': post?.author?.persona_name,
    },
  }));
}

emit('onPostsLoaded', getSchemasFromPosts(posts.value as BlogPost[] || []).concat(
  getSchemasFromPosts(mainPost.value?.data.getBlogPosts?.data as BlogPost[] || []),
));

async function onSearch(str: string) {
  searchTerm.value = str;
  page.value = 1;
  try {
    const { data: searchedPosts, errors } = await getBlogPosts({
      hidden: false,
      category: category.value as string || null,
      page: page.value,
      limit: limit.value,
      searchText: str,
    });

    if (errors?.length) {
      throw new Error(errors[0].message);
    }

    data.value = { data: searchedPosts, errors };
  } catch (e: any) {
    console.log(e.message);
    EventBus.$emit('notify', {
      success: false,
      msg: `Something went wrong: ${e.message}`,
    });
  }
}
</script>

<style scoped>
.pt0 {
  padding-top: 0;
}
</style>
